"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import Swal from "sweetalert2"
import ExhibitionTypeService from "../../../Service/ExhibitionTypeService"
import { toast } from "react-toastify"
import type { ExhibitionType } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: ExhibitionType | null
}

const ExhibitionTypeModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<ExhibitionType, "id">>({
    name: "",
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ name?: string }>({})

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
      })
    } else {
      setForm({ name: "" })
    }
    // Clear errors when modal opens/closes or data changes
    setErrors({})
  }, [initialData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    })

    // Clear error for this field when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {}

    // Name validation
    if (!form.name.trim()) {
      newErrors.name = "Name is required"
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long"
    } else if (form.name.trim().length > 100) {
      newErrors.name = "Name must not exceed 100 characters"
    } else if (!/^[a-zA-Z0-9\s\-_&().,]+$/.test(form.name.trim())) {
      newErrors.name = "Name contains invalid characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setForm({ name: "" })
    setErrors({})
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors")
      return
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${initialData ? "update" : "create"} this Exhibition Type?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${initialData ? "update" : "create"} it!`,
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setLoading(true)

      if (initialData) {
        await ExhibitionTypeService.update(initialData.id, {
          name: form.name.trim(),
        })

        await Swal.fire({
          title: "Updated!",
          text: "Exhibition Type has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      } else {
        await ExhibitionTypeService.create({
          name: form.name.trim(),
        })

        await Swal.fire({
          title: "Created!",
          text: "Exhibition Type has been created successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })

        // Reset form after successful creation
        resetForm()
      }

      onSuccess() // refetch
      toggle()
    } catch (error: any) {
      console.error("Exhibition Type operation error:", error)

      let errorMessage = "Something went wrong. Please try again."

      // Handle specific error cases
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.name) {
        errorMessage = Array.isArray(error.response.data.name) ? error.response.data.name[0] : error.response.data.name
      }

      await Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleModalToggle = () => {
    if (!initialData) {
      resetForm()
    }
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalToggle}>
      <ModalHeader toggle={handleModalToggle}>{initialData ? "Edit" : "Add"} Exhibition Type</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            invalid={!!errors.name}
            placeholder="Enter exhibition type name"
            maxLength={100}
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
          <small className="text-muted">{form.name.length}/100 characters</small>
        </FormGroup>

        <div className="text-end mt-3">
          <Button color="secondary" onClick={handleModalToggle} className="me-2" disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading || !form.name.trim()}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : initialData ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExhibitionTypeModal
